import React, { useEffect, useRef } from 'react';
import { X, CheckCircle, Package, Tag, Ruler, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './ProductDetailModal.css';

const catImageMap = {
  Bedding:        '/assets/products/bedding.png',
  Towels:         '/assets/products/towels.png',
  'Patient Wear': '/assets/products/patient-wear.png',
  Specialty:      '/assets/products/specialty.png',
};

export default function ProductDetailModal({ product, onClose, onRequireAuth }) {
  const { addItem } = useCart();
  const { isLoggedIn, isProfileComplete } = useAuth();
  const overlayRef = useRef();

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!product) return null;

  const imgSrc = product.image || catImageMap[product.cat] || '/assets/products/bedding.png';

  const handleAddToCart = () => {
    if (!isLoggedIn || !isProfileComplete) {
      onClose();
      onRequireAuth();
      return;
    }
    addItem({ ...product, image: imgSrc });
    onClose();
  };

  const handleOverlay = (e) => { if (e.target === overlayRef.current) onClose(); };

  return (
    <div className="pdm-overlay" ref={overlayRef} onClick={handleOverlay}>
      <div className="pdm-modal" role="dialog" aria-modal="true" aria-label={product.name}>
        <button className="pdm-close" onClick={onClose} aria-label="Close"><X size={18} /></button>

        <div className="pdm-image-wrap">
          <img src={imgSrc} alt={product.name} />
          {product.badge && <span className="pdm-badge">{product.badge}</span>}
        </div>

        <div className="pdm-content">
          <div className="pdm-cat">{product.cat}</div>
          <h2 className="pdm-title">{product.name}</h2>

          {product.price && (
            <div className="pdm-price">
              <span className="pdm-price-from">from</span>
              <span className="pdm-price-val">{product.price}</span>
              <span className="pdm-price-unit">/ unit</span>
            </div>
          )}

          <div className="pdm-specs">
            {product.material && (
              <div className="pdm-spec-row">
                <Tag size={14} />
                <span><strong>Material:</strong> {product.material}</span>
              </div>
            )}
            {product.spec && (
              <div className="pdm-spec-row">
                <Package size={14} />
                <span><strong>Specification:</strong> {product.spec}</span>
              </div>
            )}
            {product.sizes && (
              <div className="pdm-spec-row">
                <Ruler size={14} />
                <span><strong>Sizes:</strong> {product.sizes}</span>
              </div>
            )}
            {product.feature && (
              <div className="pdm-spec-row highlight">
                <Zap size={14} />
                <span><strong>Key feature:</strong> {product.feature}</span>
              </div>
            )}
          </div>

          <div className="pdm-compliance">
            <CheckCircle size={14} />
            <span>Meets CDC, OSHA, HLAC & FDA textile standards</span>
          </div>

          <button className="pdm-add-btn" onClick={handleAddToCart}>
            {isLoggedIn && isProfileComplete ? 'Add to Cart' : 'Sign In to Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  );
}
